import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Check, ChevronDown, Copy, Download, Loader2, MoreHorizontal, PanelRight, Plus, Redo2, Trash2, Undo2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { ApiError, api, changedElsewhere } from "../../api/client";
import type { OdbDetail, OdbIssue, OdbKind, OdbNode, OdbPreview, OdbVocabulary } from "../../api/types";
import { designate } from "../../components/attention";
import { Banner, Callout } from "../../components/Callout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { EmptyState } from "../../components/EmptyState";
import { ESCAPE, topEscapePriority } from "../../components/escapeStack";
import { Menu, MenuItem } from "../../components/Menu";
import { Disclosure } from "../../components/panel";
import { motion, Motion, Presence } from "../../components/presence";
import { SidePanel } from "../../components/SidePanel";
import { Badge, Button, IconButton, redoShortcut, shortcut } from "../../components/ui";
import { useDismiss } from "../../components/useDismiss";
import { useMediaQuery } from "../../components/useMediaQuery";
import { ACTIVE, atLeast, odbKindOf } from "../../echelons";
import { unitDesignation } from "../../i18n/designation";
import { t, tc, tServer } from "../../i18n/i18n";
import { useTrialExport } from "../../licence/trial";
import { historyKeyAction, isEditableTarget } from "../../map/useHistoryKeys";
import { plural } from "../../plural";
import { accountsItem, itemOwned } from "../../sharing/access";
import { InUseDialog, inUseOf, type InUse } from "../../sharing/InUseDialog";
import { VisibilityControl } from "../../sharing/VisibilityControl";
import { ORDER_DEPENDENT_KEYS, odbRefreshed } from "../../map/orderSync";
import { useUi } from "../../state/ui";
import { clearLocalCopy, readLocalCopy, useUnsavedDraft } from "../../state/unsavedDrafts";
import { App6Palette } from "./App6Palette";
import { bi, entryFor, paletteOf } from "./app6";
import { useOdbClipboard } from "./clipboard";
import { describeUnits, issuesOf, localIssues, mergeIssues, sortIssues } from "./editorModel";
import { duplicableEniUnit, editorKind, ENI_KIND_BY_LEVEL, isEniKind, isGenericEniOdb } from "./eni";
import { canRedo, canUndo, createHistory, type History, record, redo, seal, undo } from "./history";
import { nationName } from "./nations";
import { modelLabel as odbModelLabel, natureLabel, unitCount } from "./OdbList";
import { drawableNumber } from "./numbering";
import { OdbTour } from "./OdbTour";
import type { OrbatsOrigin } from "./orbatsTarget";
import {
  addUnits,
  countUnits,
  fitEchelonAt,
  hydrate,
  insertAt,
  nodeAt,
  nodeAtOrNull,
  type Path,
  pathFromKey,
  pathKey,
  removeAt,
  type TreeContext,
  updateAt,
  withoutCodes,
} from "./tree";
import { UnitInspector } from "./UnitInspector";
import { type TreeApply, UnitTree } from "./UnitTree";
import { UnitSymbol } from "./UnitSymbol";

/** Éditeur d'un ordre de bataille (amis APP-6 et ennemis) : arbre des unités à gauche, fiche de l'unité choisie à
 *  droite (en tiroir sur tablette, en feuille du bas sur téléphone). L'utilisateur choisit pour chaque unité son
 *  échelon et sa fonction APP-6 et ses numéros (bas gauche, bas droite) ; le serveur en déduit types, spécialités et
 *  codes, et vérifie la structure à chaque modification (`POST /odbs/preview`, après une pause de saisie).
 *
 *  Annuler / Rétablir (⌘Z, ⇧⌘Z ou Ctrl+Y) sur 100 brouillons. Enregistrement : copie locale de la saisie, registre
 *  des saisies non enregistrées, version de l'ordre de bataille (412 « Modifié par un autre poste »), refus par
 *  ricochet (409) — inchangés. */

/** Copie locale de la saisie d'un ordre de bataille : `generex-odb-draft:<code>` (rangée sous la licence). */
const ODB_DRAFT_PREFIX = "generex-odb-draft:";

/** Téléchargement d'un fichier servi en pièce jointe (le navigateur l'enregistre sans quitter la page). */
function download(url: string) {
  const link = document.createElement("a");
  link.href = url;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/** Clés repliées à l'ouverture : au-delà des subordonnés directs de ses subordonnés. */
function initialCollapse(tree: OdbNode): Set<string> {
  const keys = new Set<string>();
  const walk = (node: OdbNode, path: Path) => {
    if (path.length >= 2 && node.children.length) keys.add(pathKey(path));
    node.children.forEach((c, i) => walk(c, [...path, i]));
  };
  walk(tree, []);
  return keys;
}

export function OdbEditor({
  code,
  vocabulary,
  origin,
  onDuplicate,
  onDeleted,
  onEmploy,
}: {
  code: string;
  vocabulary: OdbVocabulary;
  /** Dossier d'où vient la demande : « Employer dans le dossier » après enregistrement. */
  origin: OrbatsOrigin | null;
  onDuplicate: (unit: string | null) => void;
  onDeleted: () => void;
  onEmploy: (value: string, odb: OdbDetail) => void;
}) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const trialExport = useTrialExport();
  const detail = useQuery({ queryKey: ["odb", code], queryFn: () => api.odb(code) });
  const data = detail.data;
  const kind = editorKind(data?.kind) ?? undefined;
  const enemy = isEniKind(kind);
  const side = enemy ? "ENI" : "AMI";
  const palette = paletteOf(vocabulary);
  const ctx: TreeContext | null = useMemo(() => (kind ? { kind, palette, vocabulary } : null), [kind, palette, vocabulary]);
  const [history, setHistory] = useState<History<OdbNode | null>>(() => createHistory<OdbNode | null>(null));
  const draft = history.present;
  const [baseline, setBaseline] = useState("");
  // Version de l'ordre de bataille sur laquelle porte le brouillon, renvoyée à l'enregistrement : un autre poste qui
  // l'a enregistré entre-temps fait refuser l'enregistrement (412) au lieu d'être écrasé.
  const [baseVersion, setBaseVersion] = useState(0);
  // Version plus récente enregistrée par un autre poste pendant la saisie (le brouillon est gardé), et brouillon mis
  // de côté pendant qu'on regarde cette version.
  const [remote, setRemote] = useState<OdbDetail | null>(null);
  const [mine, setMine] = useState<OdbNode | null>(null);
  const editing = useRef({ dirty: false, stashed: false, version: 0, accept: false, loaded: false, notified: false });
  const copyKey = `${ODB_DRAFT_PREFIX}${code}`;
  const bannerRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<OdbPreview | null>(null);
  const [previewOf, setPreviewOf] = useState<OdbNode | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string> | null>(null);
  const [selected, setSelected] = useState("0");
  const [paletteFor, setPaletteFor] = useState<string | null>(null);
  const [sheet, setSheet] = useState(false);
  const [actions, setActions] = useState(false);
  const [issuesOpen, setIssuesOpen] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [inUse, setInUse] = useState<string | null>(null);
  const [blockedBy, setBlockedBy] = useState<InUse | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const issuesRef = useRef<HTMLDivElement>(null);
  const clipboard = useOdbClipboard();
  const wide = useMediaQuery("(min-width: 1024px)");
  const roomy = useMediaQuery("(min-width: 1440px)");
  const tablet = useMediaQuery("(min-width: 768px)");
  useDismiss(sheet && !tablet, () => setSheet(false));

  /** Brouillon remplacé sans entrée d'Annuler (lecture, version d'un autre poste). */
  const resetDraft = (tree: OdbNode | null) => setHistory(createHistory(tree));
  /** Modification de l'utilisateur : une entrée d'Annuler (les frappes d'un même champ se fondent). */
  const edit = (fn: (tree: OdbNode) => OdbNode, group: string | null = null) =>
    setHistory((h) => (h.present ? record(h, fn(h.present), group) : h));

  useEffect(() => {
    if (!data?.tree || !ctx) return;
    const state = editing.current;
    const tree = hydrate(data.tree, data.symbols, ctx);
    if (state.dirty && !state.accept) {
      // Relu pendant la saisie : le brouillon n'est jamais remplacé ; une version d'un autre poste est signalée.
      if (data.version !== state.version) setRemote(data);
      return;
    }
    if (state.stashed && !state.accept) {
      if (data.version !== state.version) {
        resetDraft(tree);
        setBaseline(JSON.stringify(tree));
        setBaseVersion(data.version);
        setPreview(null);
      }
      return;
    }
    const accepted = state.accept;
    state.accept = false;
    // Première lecture : une saisie d'une session précédente est rétablie (jamais enregistrée d'office).
    const copy = state.loaded ? null : readLocalCopy<OdbNode>(copyKey);
    const restore = copy !== null && data.editable && JSON.stringify(copy.value) !== JSON.stringify(tree);
    const copyBase = restore && typeof copy.base === "number" ? copy.base : null;
    // Enregistré ici : la pile d'Annuler continue sur l'arbre enregistré (une annulation rouvre une modification).
    if (accepted) setHistory((h) => ({ ...h, present: tree }));
    else resetDraft(restore ? copy.value : tree);
    setBaseline(JSON.stringify(tree));
    setBaseVersion(copyBase ?? data.version);
    setRemote(restore && copyBase !== data.version ? data : null);
    setMine(null);
    setPreview(null);
    if (restore && !state.notified) {
      state.notified = true;
      notify({
        text: t("Saisie retrouvée"),
        detail: t("Les modifications non enregistrées de cet ordre de bataille sont rétablies : enregistrez-les ou annulez-les."),
        tone: "info",
      });
    } else if (copy && !restore) clearLocalCopy(copyKey);
    if (data.numbers_proposed && data.editable && !accepted)
      notify({ text: t("Numéros proposés"), detail: tServer("Numéros proposés d'après les noms des unités : vérifiez-les, puis enregistrez."), tone: "info" });
    setCollapsed((current) => current ?? initialCollapse(tree));
  }, [data, ctx]);

  const dirty = draft !== null && JSON.stringify(draft) !== baseline;
  const stashed = mine !== null;
  editing.current.dirty = dirty;
  editing.current.stashed = stashed;
  if (draft !== null) editing.current.loaded = true;
  editing.current.version = baseVersion;

  // Aperçu du serveur (codes, symboles, contrôles, place dans la manœuvre) après une courte pause de saisie.
  useEffect(() => {
    if (!draft || !kind || !dirty) {
      setPreview(null);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      api.previewOdb(kind, draft, controller.signal).then((result) => {
        setPreview(result);
        setPreviewOf(draft);
      }, () => undefined);
    }, 350);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [draft, kind, dirty]);

  const check = dirty ? preview : data;
  const aligned = !dirty ? Boolean(data) : Boolean(preview && previewOf === draft);
  const local = useMemo(() => (draft ? localIssues(draft) : []), [draft]);
  // Contrôles du serveur : ceux de l'aperçu aligné sur le brouillon ; entre deux aperçus, les précédents sans
  // chemin (leurs chemins ne désignent peut-être plus les mêmes unités).
  const serverIssues = useMemo(() => {
    const all = issuesOf(check);
    return aligned ? all : all.map((i) => ({ ...i, path: null, field: null }));
  }, [check, aligned]);
  const issues = useMemo(() => sortIssues(mergeIssues(local, serverIssues)), [local, serverIssues]);
  const errors = issues.filter((i) => i.level === "error");
  const toCheck = issues.filter((i) => i.level !== "info").length;
  const infos = useMemo(
    () =>
      draft && ctx
        ? describeUnits(draft, ctx, aligned && check ? { symbols: check.symbols, tree: check.tree } : null, issues)
        : new Map(),
    [draft, ctx, aligned, check, issues],
  );

  const saveButton = useRef<HTMLDivElement>(null);
  const savable = dirty && !stashed && remote === null && preview !== null && previewOf === draft && errors.length === 0;
  const dropDraft = useUnsavedDraft(data?.editable ? `odb:${code}` : null, dirty || stashed, {
    label: t("Ordre de bataille « {name} »", { name: (mine ?? draft)?.name || data?.name || code }),
    save: savable ? () => save.mutateAsync() : undefined,
    discard: () => {
      setMine(null);
      if (remote?.tree && ctx) {
        const tree = hydrate(remote.tree, remote.symbols, ctx);
        resetDraft(tree);
        setBaseline(JSON.stringify(tree));
        setBaseVersion(remote.version);
        setRemote(null);
      } else resetDraft(JSON.parse(baseline || "null") as OdbNode | null);
      setPreview(null);
    },
    reveal: () => designate(saveButton.current?.querySelector("button:last-child") ?? null),
    copy: { key: copyKey, value: mine ?? draft, base: stashed ? null : baseVersion },
    keep: true,
  });

  // Ordre de bataille enregistré (ami ou ennemi) : les dossiers qui l'emploient changent de dispositif, d'ordre de
  // bataille de la carte et d'OPO (recomposé par le serveur, ou à sa prochaine ouverture) ; tout ce qui en dépend est
  // relu.
  const refreshLists = () => {
    void queryClient.invalidateQueries({ queryKey: ["odbs"] });
    void queryClient.invalidateQueries({ queryKey: ["forces"] });
    void queryClient.invalidateQueries({ queryKey: ["exercises"] });
    void queryClient.invalidateQueries({ queryKey: ["deployment"] });
    void queryClient.invalidateQueries({ queryKey: ["orbat"] });
    for (const key of ORDER_DEPENDENT_KEYS) void queryClient.invalidateQueries({ queryKey: [key] });
  };

  const save = useMutation({
    mutationFn: () => api.updateOdb(code, kind as OdbKind, draft as OdbNode, baseVersion),
    onSuccess: (odb: OdbDetail) => {
      editing.current.accept = true; // l'ordre de bataille enregistré remplace le brouillon
      queryClient.setQueryData(["odb", code], odb);
      refreshLists();
      // Dossiers qui l'emploient : leurs OPO ont été recomposés ; celui du dossier ouvert mène à ses changements.
      const touched = odbRefreshed(odb.refreshed);
      notify({
        text: t("Ordre de bataille enregistré"),
        detail: [
          odb.warnings.length
            ? t("{n} point(s) à vérifier avant de s'en servir.", { n: odb.warnings.length })
            : t("Il sert dès maintenant dans les Données initiales."),
          touched.note,
        ]
          .filter(Boolean)
          .join(" "),
        tone: odb.warnings.length ? "warn" : "ok",
        action: touched.action,
      });
    },
    onError: (e: Error) => {
      if (changedElsewhere(e)) {
        notify({
          text: t("Modifié par un autre poste"),
          detail: t("Cet ordre de bataille a été modifié par un autre poste : vos modifications sont gardées."),
          tone: "warn",
        });
        void queryClient.invalidateQueries({ queryKey: ["odb", code] });
        return;
      }
      const others = inUseOf(e);
      if (others) {
        setBlockedBy(others);
        return;
      }
      notify({ text: t("Enregistrement impossible"), detail: tServer(e.message), tone: "error" });
    },
  });

  const remove = useMutation({
    mutationFn: (force: boolean) => api.deleteOdb(code, force),
    onSuccess: (refreshed) => {
      setConfirmDelete(false);
      setInUse(null);
      dropDraft();
      refreshLists();
      // Suppression forcée : les dossiers qui l'employaient ont leurs OPO recomposés (ennemi de référence, ou ordre
      // de bataille du catalogue).
      const touched = odbRefreshed(refreshed);
      notify({ text: t("Ordre de bataille supprimé"), detail: touched.note || undefined, tone: "info", action: touched.action });
      onDeleted();
    },
    onError: (e: Error) => {
      setConfirmDelete(false);
      const others = inUseOf(e);
      if (others) {
        setInUse(null);
        setBlockedBy(others);
      } else if (e instanceof ApiError && e.code === "ODB_IN_USE") setInUse(e.message);
      else notify({ text: t("Suppression impossible"), detail: e.message, tone: "error" });
    },
  });

  // ⌘Z / ⇧⌘Z / Ctrl+Y : Annuler et Rétablir de l'éditeur (hors saisie, fenêtre ou menu ouverts).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const action = historyKeyAction(e);
      if (!action || e.defaultPrevented || !data?.editable) return;
      if (topEscapePriority() >= ESCAPE.menu) return;
      if (isEditableTarget(e.target) || isEditableTarget(document.activeElement)) return;
      e.preventDefault();
      setHistory((h) => (action === "undo" ? undo(h) : redo(h)));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [data?.editable]);

  // L'unité sélectionnée existe toujours (un retrait, une annulation peuvent l'avoir ôtée).
  useEffect(() => {
    if (draft && !nodeAtOrNull(draft, pathFromKey(selected))) setSelected("0");
  }, [draft, selected]);

  if (detail.isError) return <EmptyState variant="error">{detail.error.message}</EmptyState>;
  if (!data || !draft || !kind || !collapsed || !ctx) return <EmptyState variant="loading" />;

  const editable = data.editable;
  const kindInfo = vocabulary.kinds.find((k) => k.key === kind);
  const total = countUnits(draft);
  const rootInfo = infos.get("0");
  const current = infos.get(selected) ?? rootInfo;
  const apply: TreeApply = (tree, options = {}) => {
    edit(() => tree, options.group ?? null);
    if (options.expand) {
      const key = pathKey(options.expand);
      setCollapsed((c) => {
        if (!c?.has(key)) return c;
        const next = new Set(c);
        next.delete(key);
        return next;
      });
    }
    if (options.select) setSelected(pathKey(options.select));
  };
  const toggle = (key: string, open?: boolean) =>
    setCollapsed((c) => {
      const next = new Set(c);
      const isOpen = !next.has(key);
      if ((open ?? !isOpen) === true) next.delete(key);
      else next.add(key);
      return next;
    });
  const selectUnit = (key: string) => {
    setSelected(key);
    if (!wide) setSheet(true);
  };
  const removeUnit = (path: Path) => {
    const name = nodeAt(draft, path).name.trim() || infos.get(pathKey(path))?.code || t("Sans nom");
    apply(removeAt(draft, path), { select: path.slice(0, -1) });
    notify({
      text: t("Unité retirée"),
      detail: t("« {name} » et ses subordonnés.", { name }),
      tone: "info",
      action: { label: tc("défaire", "Annuler"), run: () => setHistory((h) => undo(h)) },
    });
  };
  const paste = (path: Path) => {
    if (!clipboard.node || clipboard.side !== side) {
      notify({ text: t("Copiez d'abord une branche d'un ordre de bataille du même camp."), tone: "info" });
      return;
    }
    const size = nodeAt(draft, path).children.length;
    // Branche collée sous une formation de même échelon ou inférieure : échelons ramenés sous elle (R1).
    const pasted = fitEchelonAt(insertAt(draft, path, size, withoutCodes(clipboard.node)), [...path, size], ctx);
    apply(pasted.tree, { select: [...path, size], expand: path });
  };
  const changeSelected = (fn: (node: OdbNode) => OdbNode, group?: string) => {
    const path = pathFromKey(current?.key ?? "0");
    edit((tree) => updateAt(tree, path, fn), group ?? null);
  };
  const catalogueDuplicate = (info: { node: OdbNode; rank: string | null }) => {
    const unit = info.node.code;
    if (editable || !unit) return undefined;
    if (enemy ? !duplicableEniUnit(unit, data.subtrees) : info.rank !== "GTIA") return undefined;
    return () => onDuplicate(unit);
  };

  // Action attendue quand « Enregistrer » est bloqué : la version d'un autre poste, sinon la première erreur
  // (unité sélectionnée, champ désigné), sinon le bandeau des contrôles.
  const designateIssue = (issue: OdbIssue) => {
    if (issue.path && nodeAtOrNull(draft, pathFromKey(issue.path))) {
      setSelected(issue.path);
      if (!wide) setSheet(true);
      const field = issue.field;
      window.setTimeout(() => {
        const target =
          (field && editorRef.current?.querySelector<HTMLElement>(`[data-inspector] [data-field="${field}"]`)) ||
          document.querySelector<HTMLElement>(`[data-inspector] [data-field="${field ?? "name"}"]`) ||
          editorRef.current?.querySelector<HTMLElement>(`[data-unit-row="${issue.path}"]`);
        designate(target ?? null, field ? "field" : "ring");
      }, 60);
      return;
    }
    setIssuesOpen(true);
    designate(issuesRef.current, "ring");
  };
  const showFault = () => {
    if (remote) {
      designate(bannerRef.current);
      notify({ text: t("Choisissez d'abord : voir sa version ou garder les vôtres."), tone: "info" });
      return;
    }
    const first = errors[0];
    if (first) designateIssue(first);
    notify({ text: t("Corrigez d'abord le point signalé."), tone: "info" });
  };
  const viewTheirs = () => {
    if (!remote?.tree) return;
    setMine((kept) => kept ?? draft);
    const tree = hydrate(remote.tree, remote.symbols, ctx);
    resetDraft(tree);
    setBaseline(JSON.stringify(tree));
    setBaseVersion(remote.version);
    setRemote(null);
    setPreview(null);
  };
  const keepMine = () => {
    if (!remote) return;
    setBaseVersion(remote.version);
    setRemote(null);
  };
  const backToMine = () => {
    if (mine) edit(() => mine);
    setMine(null);
  };

  // S/GTIA employé par un dossier de compagnie : celui qui est sélectionné, sinon le premier du GTIA enregistré.
  const companies = (data.tree?.children ?? []).filter((n) => (n.type ?? "").toUpperCase() === "SGTIA" && n.code);
  const selectedCompany = current?.rank === "SGTIA" && current.node.code ? companies.find((n) => n.code === current.node.code) : undefined;
  const employedCompany = selectedCompany ?? companies[0] ?? null;
  // « Employer dans le dossier » : ordre de bataille enregistré, de la nature qui fait face au niveau du dossier.
  const employValue = (() => {
    if (!origin || dirty || stashed || !editable) return null;
    if (origin.field === "eni_odb") {
      // Compagnie (docs/21 §4) : l'ennemi du GTIA, un bataillon.
      const expected = ENI_KIND_BY_LEVEL[origin.level];
      return kind === expected ? code : null;
    }
    if (origin.level === "REGIMENT") return kind === "gtia" && data.tree?.code ? `${code}/${data.tree.code}` : null;
    // Compagnie (docs/21 §2) : le S/GTIA sélectionné du GTIA construit, sinon son premier S/GTIA (« ODB/GTIA/S-GTIA »).
    if (origin.level === "COMPAGNIE") return kind === "gtia" && data.tree?.code && employedCompany ? `${code}/${data.tree.code}/${employedCompany.code}` : null;
    return atLeast(origin.level, "BRIGADE") && kind === odbKindOf(origin.level) ? code : null;
  })();
  // Dossier qui affronte l'ennemi générique (force non française, registre B39) : seul un ordre de bataille ennemi
  // générique y convient (le serveur refuse les autres).
  const employRefused =
    employValue !== null && origin?.field === "eni_odb" && origin.referenceEnemy === false && !isGenericEniOdb(data);
  const emptyRoot = draft.children.length === 0 && editable;
  const modelLabel = data.model?.label && bi(data.model.label) !== draft.name ? odbModelLabel(data.model.source, bi(data.model.label)) : null;
  const nation = nationName(draft.nation);
  const sidcRoot = rootInfo?.sidc ?? data.sidc;
  const inspector = current && (
    <div data-inspector="">
      <UnitInspector
        key={current.key}
        info={current}
        tree={draft}
        ctx={ctx}
        enemy={enemy}
        editable={editable}
        vocabulary={vocabulary}
        rootName={draft.name}
        onChange={changeSelected}
        onApplyTree={(tree) => apply(tree)}
        onOpenPalette={() => setPaletteFor(current.key)}
        onSeal={() => setHistory((h) => seal(h))}
        onClose={wide || tablet ? undefined : () => setSheet(false)}
      />
    </div>
  );
  const paletteUnit = paletteFor ? infos.get(paletteFor) : null;
  // Racine seule : autant de subordonnés que la manœuvre de son niveau a d'emplacements (3 S/GTIA au régiment, 4 GTIA
  // à la brigade, 4 brigades à la division) — suivre le bouton ne laisse aucun avertissement d'emplacements.
  const firstSlots = ACTIVE.find((e) => e.odb_kind === kind)?.slots ?? 3;

  return (
    <div ref={editorRef} className={clsx("relative flex h-full min-h-0 flex-col", motion("fade"))}>
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface px-4 py-2.5">
        <span className="flex h-14 w-16 shrink-0 items-center justify-center">
          <UnitSymbol sidc={sidcRoot} size={20} left={drawableNumber(rootInfo?.number)} right={drawableNumber(rootInfo?.higher.value)} />
        </span>
        <div className="min-w-48 flex-1">
          {/* Le nom d'abord : le badge du modèle d'origine se tronque avant lui (il plafonne à 45 % de la ligne). */}
          <h2 className="flex min-w-0 items-center gap-2 text-heading font-semibold text-ink">
            <span className="min-w-0 truncate" title={unitDesignation(draft.name) || undefined}>
              {unitDesignation(draft.name) || t("Sans nom")}
            </span>
            {modelLabel && (
              <Badge size="sm" tone={enemy ? "hostile" : "friend"} className="min-w-0 max-w-[45%] shrink-4 max-sm:hidden" title={modelLabel}>
                <span className="min-w-0 truncate">{modelLabel}</span>
              </Badge>
            )}
          </h2>
          <p className="truncate text-meta text-ink-muted">
            {kindInfo ? natureLabel(kind, kindInfo.label, draft.organisation) : ""}
            {nation ? ` · ${nation}` : ""} · {unitCount(total)}
            {!editable &&
              ` — ${
                data.user
                  ? t("Seuls son auteur et les administrateurs de l'unité peuvent modifier cet élément : dupliquez-le pour l'adapter.")
                  : t("ordre de bataille du catalogue : dupliquez-le pour l'adapter.")
              }`}
          </p>
        </div>
        {/* Commandes groupées : elles passent ensemble à la ligne (téléphone), jamais une icône seule. */}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        {editable && (
          <span className="flex items-center gap-0.5">
            <IconButton
              size="sm"
              disabled={!canUndo(history)}
              onClick={() => setHistory((h) => undo(h))}
              title={t("Annuler ({key})", { key: shortcut("Z") })}
              aria-label={tc("défaire", "Annuler")}
            >
              <Undo2 size={16} />
            </IconButton>
            <IconButton
              size="sm"
              disabled={!canRedo(history)}
              onClick={() => setHistory((h) => redo(h))}
              title={t("Rétablir ({key})", { key: redoShortcut() })}
              aria-label={tc("refaire", "Rétablir")}
            >
              <Redo2 size={16} />
            </IconButton>
          </span>
        )}
        {data.user && accountsItem(data) && (
          <VisibilityControl
            item={data}
            users={data.used_by ?? 0}
            onChange={(visibility) => api.odbVisibility(code, visibility)}
            onSaved={(next) => {
              queryClient.setQueryData(["odb", code], next);
              refreshLists();
            }}
          />
        )}
        {tablet ? (
          <>
            {data.user && (editable || accountsItem(data)) && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => download(api.odbExportUrl(code))}
                blocked={trialExport.blocked}
                onBlocked={trialExport.onBlocked}
                title={trialExport.title ?? t("Fichier JSON au format de l'application Generex pour iPad")}
              >
                <Download size={14} /> {t("Exporter")}
              </Button>
            )}
            {data.user && (
              <Button size="sm" variant="secondary" onClick={() => onDuplicate(null)} title={t("Copie modifiable de cet ordre de bataille")}>
                <Copy size={14} /> {t("Dupliquer")}
              </Button>
            )}
            {data.user && editable && itemOwned(data) && (
              <Button size="sm" variant="danger-ghost" onClick={() => setConfirmDelete(true)}>
                <Trash2 size={14} /> {t("Supprimer")}
              </Button>
            )}
          </>
        ) : (
          // Téléphone : les actions de l'ordre de bataille dans « ⋯ ».
          data.user && (
            <Menu
              open={actions}
              onClose={() => setActions(false)}
              align="end"
              width={240}
              portal
              ariaLabel={t("Actions sur l'ordre de bataille")}
              trigger={
                <IconButton size="sm" onClick={() => setActions((o) => !o)} title={t("Plus d'actions")} aria-label={t("Plus d'actions")}>
                  <MoreHorizontal size={16} />
                </IconButton>
              }
            >
              <MenuItem icon={PanelRight} label={t("Fiche de l'unité")} onSelect={() => setSheet(true)} />
              {(editable || accountsItem(data)) && <MenuItem icon={Download} label={t("Exporter")} onSelect={() => download(api.odbExportUrl(code))} blocked={trialExport.blocked} onBlocked={trialExport.onBlocked} title={trialExport.title} hint={trialExport.title} />}
              <MenuItem icon={Copy} label={t("Dupliquer")} onSelect={() => onDuplicate(null)} />
              {editable && itemOwned(data) && <MenuItem icon={Trash2} danger label={t("Supprimer")} onSelect={() => setConfirmDelete(true)} />}
            </Menu>
          )
        )}
        {!editable && !data.user && (
          <Button size="sm" highlight onClick={() => onDuplicate(null)}>
            <Copy size={14} /> {t("Dupliquer pour l'adapter")}
          </Button>
        )}
        {!wide && (tablet || !data.user) && (
          <IconButton size="sm" onClick={() => setSheet(true)} title={t("Fiche de l'unité")} aria-label={t("Fiche de l'unité")}>
            <PanelRight size={16} />
          </IconButton>
        )}
        </div>
      </div>

      <Presence show={remote !== null}>
        {remote && (
          <div ref={bannerRef}>
            <Banner
              tone="warn"
              actions={
                <>
                  <Button size="sm" variant="secondary" onClick={viewTheirs}>
                    {t("Voir sa version")}
                  </Button>
                  <Button size="sm" onClick={keepMine}>
                    {t("Garder les miennes")}
                  </Button>
                </>
              }
            >
              {t("Cet ordre de bataille a été modifié par un autre poste : vos modifications sont gardées.")}
            </Banner>
          </div>
        )}
      </Presence>
      <Presence show={mine !== null && remote === null}>
        {mine && remote === null && (
          <Banner
            tone="info"
            actions={
              <Button size="sm" variant="secondary" onClick={backToMine}>
                <Undo2 size={14} /> {t("Revenir à mes modifications")}
              </Button>
            }
          >
            {t("Vous voyez la version de l'autre poste ; vos modifications sont gardées de côté.")}
          </Banner>
        )}
      </Presence>
      <Presence show={employValue !== null}>
        {employValue !== null && origin && (
          <Banner
            tone={employRefused ? "neutral" : "accent"}
            actions={
              <Button
                size="sm"
                highlight={!employRefused}
                disabled={employRefused}
                onClick={() => onEmploy(employValue, data)}
              >
                <Check size={14} /> {t("Employer dans le dossier")}
              </Button>
            }
          >
            {employRefused
              ? t("Seuls les ordres de bataille ennemis génériques conviennent à ce dossier.")
              : origin.level === "COMPAGNIE" && origin.field === "odb" && employedCompany
              ? t("Employer « {unit} » de « {odb} » dans le dossier : sélectionnez dans l'arbre un autre S/GTIA pour le changer. Le choix est reporté dans les Données initiales, à enregistrer.", {
                  unit: unitDesignation(employedCompany.name),
                  odb: unitDesignation(draft.name),
                })
              : origin.exerciseName
              ? t("Employer « {odb} » dans le dossier « {name} » : le choix est reporté dans ses Données initiales, à enregistrer.", {
                  odb: unitDesignation(draft.name),
                  name: origin.exerciseName,
                })
              : t("Employer « {odb} » dans le nouveau dossier : le choix est reporté dans ses Données initiales, à enregistrer.", { odb: unitDesignation(draft.name) })}
          </Banner>
        )}
      </Presence>

      {/* Contrôles : bandeau repliable, chaque point renvoie à son unité et à son champ. */}
      <Presence show={issues.length > 0}>
        {issues.length > 0 && (
          <Motion origin="top" ref={issuesRef} className="shrink-0 border-b border-line bg-surface">
            <button
              type="button"
              aria-expanded={issuesOpen}
              onClick={() => setIssuesOpen((o) => !o)}
              className={clsx(
                "flex min-h-9 w-full items-center gap-2 px-4 py-1.5 text-left text-ui font-medium transition-colors hover:bg-hover",
                errors.length ? "text-danger" : toCheck ? "text-warn" : "text-ink-muted",
              )}
            >
              <ChevronDown size={14} aria-hidden className={clsx("shrink-0 transition-transform duration-(--gx-enter)", !issuesOpen && "-rotate-90")} />
              {/* Seuls les erreurs et avertissements sont « à vérifier » ; les informations (unités non placées,
                  unité de référence gardée par la manœuvre) n'appellent aucune correction. */}
              <span className="min-w-0 flex-1">
                {toCheck ? t("{n} point(s) à vérifier", { n: toCheck }) : plural(issues.length, t("information"), t("informations"))}
              </span>
              {errors.length > 0 && (
                <Badge size="sm" tone="danger">
                  {t("{n} à corriger", { n: errors.length })}
                </Badge>
              )}
            </button>
            <Disclosure open={issuesOpen}>
              <ul className="max-h-40 space-y-0.5 overflow-y-auto px-4 pb-2.5">
                {issues.map((issue, i) => (
                  <li key={`${issue.code}-${issue.path}-${i}`}>
                    <button
                      type="button"
                      onClick={() => designateIssue(issue)}
                      className={clsx(
                        "flex min-h-7 w-full items-start gap-2 rounded-md px-1.5 py-1 text-left text-meta leading-snug transition-colors hover:bg-hover",
                        issue.level === "error" ? "text-danger" : issue.level === "warning" ? "text-warn" : "text-ink-muted",
                      )}
                    >
                      <span aria-hidden className="mt-1.5 size-1.5 shrink-0 rounded-full bg-current" />
                      <span className="min-w-0 flex-1">{tServer(issue.message)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </Disclosure>
          </Motion>
        )}
      </Presence>

      <div className="relative flex min-h-0 flex-1">
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          <Presence show={emptyRoot}>
            {emptyRoot && (
              <Motion origin="fade" className="px-4 pt-3">
                <Callout tone="info">
                  <span className="flex flex-wrap items-center justify-between gap-2">
                    <span>{t("Ajoutez les subordonnés de « {name} ».", { name: unitDesignation(draft.name) || t("Sans nom") })}</span>
                    {rootInfo?.childEchelon && (
                      <Button size="sm" highlight onClick={() => apply(addUnits(draft, [], ctx, firstSlots), { select: [0] })}>
                        <Plus size={14} /> {t("Ajouter {n} subordonnés", { n: firstSlots })}
                      </Button>
                    )}
                  </span>
                </Callout>
              </Motion>
            )}
          </Presence>
          <UnitTree
            tree={draft}
            ctx={ctx}
            infos={infos}
            enemy={enemy}
            editable={editable}
            selected={current?.key ?? "0"}
            collapsed={collapsed}
            canPaste={Boolean(clipboard.node && clipboard.side === side)}
            onSelect={selectUnit}
            onToggle={toggle}
            apply={apply}
            onSeal={() => setHistory((h) => seal(h))}
            onRemove={removeUnit}
            onCopy={(node) => {
              clipboard.copy(side, node);
              notify({ text: t("Branche copiée"), detail: t("« {name} » : collez-la comme subordonné (⌘V).", { name: unitDesignation(node.name) || t("Sans nom") }), tone: "info" });
            }}
            onPaste={paste}
            onOpenPalette={(key) => setPaletteFor(key)}
            catalogueDuplicate={catalogueDuplicate}
          />
          <div className="h-16" />
        </div>
        {wide ? (
          <aside
            className={clsx("shrink-0 overflow-y-auto border-l border-line bg-surface", roomy ? "w-[360px]" : "w-[320px]")}
            aria-label={t("Fiche de l'unité")}
          >
            {inspector}
          </aside>
        ) : tablet ? (
          <Presence show={sheet}>
            {sheet && (
              <SidePanel title={t("Fiche de l'unité")} onClose={() => setSheet(false)} width={360} overlayBelow={1024}>
                {inspector}
              </SidePanel>
            )}
          </Presence>
        ) : (
          <Presence show={sheet}>
            {sheet && (
              <>
                <Motion origin="fade" className="absolute inset-0 z-30 bg-ink/20" onClick={() => setSheet(false)} aria-hidden />
                <Motion
                  origin="bottom"
                  role="dialog"
                  aria-label={t("Fiche de l'unité")}
                  className="absolute inset-x-0 bottom-0 z-40 max-h-[85vh] overflow-y-auto rounded-t-xl border-t border-line bg-surface shadow-float"
                >
                  {inspector}
                </Motion>
              </>
            )}
          </Presence>
        )}
        {/* Prise en main : dans la zone de l'arbre, à gauche de la fiche (large écran), jamais par-dessus. */}
        {editable && <OdbTour placement={wide ? (roomy ? "right-[376px] bottom-4" : "right-[336px] bottom-4") : "right-4 bottom-4"} />}
      </div>

      {editable && (
        <div className="sticky bottom-0 flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-line bg-surface px-4 py-3">
          <p className="min-w-0 text-meta text-ink-muted">
            {dirty
              ? t("Modifications non enregistrées.")
              : data.dossiers.length
                ? enemy
                  ? t("Ennemi de {n} dossier(s) : ils suivent ses modifications.", { n: data.dossiers.length })
                  : t("Force de {n} dossier(s) : ils suivent ses modifications.", { n: data.dossiers.length })
                : t("Enregistré.")}
          </p>
          <div ref={saveButton} className="flex items-center gap-2">
            {dirty && (
              <Button
                variant="secondary"
                onClick={() => {
                  if (remote?.tree) {
                    const tree = hydrate(remote.tree, remote.symbols, ctx);
                    resetDraft(tree);
                    setBaseline(JSON.stringify(tree));
                    setBaseVersion(remote.version);
                    setRemote(null);
                  } else edit(() => JSON.parse(baseline) as OdbNode);
                  setPreview(null);
                }}
              >
                <Undo2 size={14} /> {t("Annuler les modifications")}
              </Button>
            )}
            <Button
              onClick={() => save.mutate()}
              disabled={!dirty || save.isPending}
              blocked={dirty && (errors.length > 0 || remote !== null)}
              onBlocked={showFault}
              highlight={dirty && errors.length === 0 && remote === null}
            >
              {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {t("Enregistrer")}
            </Button>
          </div>
        </div>
      )}


      <Presence show={paletteUnit !== null && paletteUnit !== undefined}>
        {paletteUnit && (
          <App6Palette
            palette={palette}
            unit={{
              side,
              echelon: paletteUnit.echelon,
              hq: paletteUnit.hq,
              tf: paletteUnit.tf,
              number: drawableNumber(paletteUnit.number),
              higher: drawableNumber(paletteUnit.higher.value),
              fonction: paletteUnit.node.fonction ?? entryFor(palette, null, paletteUnit.node.app6)?.key ?? null,
              app6: paletteUnit.node.app6 ?? null,
            }}
            onClose={() => setPaletteFor(null)}
            onChoose={(entry, app6) => {
              const path = paletteUnit.path;
              setPaletteFor(null);
              edit((tree) =>
                updateAt(tree, path, (n) => ({
                  ...n,
                  fonction: entry.key,
                  app6,
                  specialty: entry.specialty ?? n.specialty ?? null,
                  manoeuvre: null,
                  ...(enemy ? { role: null } : {}),
                })),
              );
            }}
          />
        )}
      </Presence>
      <Presence show={confirmDelete}>
        {confirmDelete && (
          <ConfirmDialog
            title={t("Supprimer l'ordre de bataille ?")}
            confirmLabel={t("Supprimer")}
            busy={remove.isPending}
            busyLabel={t("Suppression…")}
            onConfirm={() => remove.mutate(false)}
            onCancel={() => setConfirmDelete(false)}
          >
            {t("« {name} » sera supprimé du serveur. Téléchargez-le d'abord (Exporter) pour le garder.", { name: data.name })}
          </ConfirmDialog>
        )}
      </Presence>
      <Presence show={blockedBy !== null}>
        {blockedBy !== null && (
          <InUseDialog
            inUse={blockedBy}
            onClose={() => setBlockedBy(null)}
            onDuplicate={() => {
              setBlockedBy(null);
              onDuplicate(null);
            }}
          />
        )}
      </Presence>
      <Presence show={inUse !== null}>
        {inUse !== null && (
          <ConfirmDialog
            title={t("Ordre de bataille utilisé")}
            confirmLabel={t("Supprimer quand même")}
            busy={remove.isPending}
            busyLabel={t("Suppression…")}
            onConfirm={() => remove.mutate(true)}
            onCancel={() => setInUse(null)}
          >
            {inUse}
          </ConfirmDialog>
        )}
      </Presence>
    </div>
  );
}
